import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowDown, MoreHoriz, PushPinOutlined } from '@mui/icons-material';
import { Checkbox, IconButton, Menu, MenuItem, Popper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty, isNil, range, sortBy, uniq } from 'lodash';
import { AI_GRADIENT_CSS, aiGradientTextSx } from 'common/constants/ai';
import AddMultipleMenuItem from 'components/FeaturedSpreadsheet/components/AddMultipleMenuItem';
import { useAIFieldAcceptContext } from 'pages/CapTable/cap-table/components/captable-table/AIFieldAcceptContext';
import { isPendingMeta } from 'pages/CapTable/cap-table/utilities/securityAiPatches';
import { SORT_DESC } from 'pages/Valuations/util/constants';
import { useResponse } from 'services/hooks';
import { useSecurityReviewBusy } from 'store/useReviewStore';
import theme from 'theme';
import { getStringValue } from 'utilities';
import { getColumnIndex, getColumnLetter } from 'utilities/alphabet-utilities';
import { SortAscIcon, SortDescIcon } from './SortIcons';
import { ReverseParsedFundOwnershipDetail } from '../../../pages/CapTable/fund-ownership/types';
import FeaturedSpreadsheetContext from '../context/FeaturedSpreadsheetContext';
import { copyToClipboard, getCellRef, getValuesToCopy, hasCellColSpan } from '../utilities/utilities';

interface StyledColumnIndicatorProps {
  allowReorderColumns?: boolean;
}

interface StyledMenuButtonProps {
  cursorType?: React.CSSProperties['cursor'];
  emphasizeViewerFreezeAction?: boolean;
}

const shouldForwardColumnIndicatorProp = (prop: PropertyKey): boolean => prop !== 'allowReorderColumns';

const shouldForwardMenuButtonProp = (prop: PropertyKey): boolean =>
  prop !== 'cursorType' && prop !== 'emphasizeViewerFreezeAction';

const iconStyles = {
  fill: `${theme.palette.white} !important`,
  width: 'auto !important',
  height: '15px !important',
};

const StyledColumnIndicator = styled('div', {
  shouldForwardProp: shouldForwardColumnIndicatorProp,
})<StyledColumnIndicatorProps>(({ allowReorderColumns }) => ({
  padding: 0,
  position: 'absolute',
  top: '0',
  backgroundColor: theme.palette.primary[500],
  borderRadius: '10px 10px 0px 0px',
  height: '12px',
  textAlign: 'center',
  cursor: allowReorderColumns ? 'move' : 'default',
}));

const StyledMenuButton = styled(IconButton, {
  shouldForwardProp: shouldForwardMenuButtonProp,
})<StyledMenuButtonProps>(({ cursorType, emphasizeViewerFreezeAction }) => ({
  maxWidth: emphasizeViewerFreezeAction ? '18px' : '12px',
  minWidth: emphasizeViewerFreezeAction ? '18px' : '12px',
  color: theme.palette.white,
  padding: '0',
  marginLeft: emphasizeViewerFreezeAction ? '-3px' : 0,
  marginTop: emphasizeViewerFreezeAction ? '-17px' : '-14px',
  height: emphasizeViewerFreezeAction ? '18px' : '12px',
  cursor: cursorType || 'default',
  '& > svg': emphasizeViewerFreezeAction
    ? {
      fontSize: '18px !important',
      height: '18px !important',
    }
    : {},
}));

const StyledMoreHorizIcon = styled(MoreHoriz)(() => iconStyles);

const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDown)(() => iconStyles);

const StyledDeleteMenuItem = styled(MenuItem)(() => ({
  color: theme.palette.pink,
}));

const StyledMultiColumnLabel = styled('span')(() => ({
  color: theme.palette.white,
  fontSize: '9px',
  fontWeight: 600,
  lineHeight: '12px',
  marginRight: '4px',
  verticalAlign: 'top',
}));

const StyledAcceptAllMenuItem = styled(MenuItem)(() => ({
  fontWeight: 600,
  '&:hover': {
    background: AI_GRADIENT_CSS,
    '& .MuiTypography-root': {
      background: 'none',
      WebkitBackgroundClip: 'unset',
      WebkitTextFillColor: 'white',
    },
  },
}));

interface StyledMenuProps {
  compactViewerOptions: boolean;
}

const StyledMenu = styled(Menu, {
  shouldForwardProp: prop => prop !== 'compactViewerOptions',
})<StyledMenuProps>(({ compactViewerOptions }) =>
  compactViewerOptions
    ? {
      '& .MuiPaper-root': {
        minWidth: '188px',
        maxWidth: '220px',
      },
      '& .MuiList-root': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        minHeight: '28px',
        padding: '3px 8px 3px 10px',
        fontSize: '12px',
        lineHeight: 1.25,
        gap: '8px',
        whiteSpace: 'nowrap',
      },
      '& .MuiMenuItem-root > .MuiSvgIcon-root': {
        flex: '0 0 auto',
        fontSize: '16px',
      },
      '& .MuiCheckbox-root': {
        marginLeft: 'auto',
        padding: '2px',
      },
      '& .MuiCheckbox-root .MuiSvgIcon-root': {
        fontSize: '16px',
      },
    }
    : {}
);

interface ColumnIndicatorProps {
  frozenPaneCellKey?: string;
  hiddenColumns?: {
    right?: number;
    left?: number;
  };
  onToggleFrozenPaneAtCell?: (cellKey: string) => void;
  tableRef: React.RefObject<HTMLElement> | null;
}

interface IndicatorPosition {
  left: number;
  width: number;
}

interface CellInfo {
  key?: string;
  columnLegend?: string | null;
  y?: number | null;
  columnId?: number | string | null;
}

interface Column {
  id?: number | string;
  order?: number;
  isLTM?: boolean;
  isNTM?: boolean;
  isDeleteableColumn?: boolean;
  isSortableColumn?: boolean;
  isStringColumn?: boolean;
  isDateColumn?: boolean;
  fields?: Array<Record<string, string>>;
  parentColumn?: any;
}

// This uses a regular expression to replace all digits with an empty string
const getLetterFromKey = (key?: string): string => key?.replace(/\d/g, '') ?? '';

// The same integer the single-column delete hands its handler: the cell's y, else its legend index.
const resolveColumnIndex = (cell: CellInfo): number | null =>
  cell.y ?? getColumnIndex(cell.columnLegend ?? getLetterFromKey(cell.key));

// Spans the indicator from the left edge of the first selected column to the right edge of the last.
const computeMultiColumnPosition = (
  tableRef: React.RefObject<HTMLElement> | null,
  cellKeys: string[]
): IndicatorPosition | null => {
  const cellRefs = cellKeys.map(key => getCellRef(tableRef, key)).filter((ref): ref is HTMLElement => Boolean(ref));
  if (cellRefs.length === 0) {
    return null;
  }
  const left = Math.min(...cellRefs.map(ref => ref.offsetLeft));
  const right = Math.max(...cellRefs.map(ref => ref.offsetLeft + ref.offsetWidth));
  return { left, width: right - left };
};

const ColumnIndicator: React.FC<ColumnIndicatorProps> = ({
  frozenPaneCellKey,
  tableRef,
  hiddenColumns,
  onToggleFrozenPaneAtCell,
}) => {
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorPosition | null>(null);
  const [isIndicatorActionable, setIsIndicatorActionable] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [cell, setCell] = useState<CellInfo | null>(null);
  const [multipleValue, setMultipleValue] = useState<number>(0);
  const {
    addMultipleColumns,
    allowAddMultipleColumns,
    allowCloneColumn,
    allowConfirmAndDeleteColumn,
    allowCopyColumn,
    allowDeleteColumn,
    allowDeleteOnlySpecificColumn,
    allowMergedSelectionIndicatorSpan,
    allowReorderColumns,
    allowSortColumn,
    cells,
    cloneColumn,
    columns,
    deleteMultipleColumnsFn,
    displayColumnIndicator,
    format,
    handleDeleteColumn,
    selectedCell,
    selectionSnapshot,
    setClearSelected,
    setSelectedCell,
    sortColumnFn,
    sortedColumn,
    workbook,
  } = useContext(FeaturedSpreadsheetContext);

  const { acceptAllFields, dismissAllFields } = useAIFieldAcceptContext();

  const { infoNotification, errorNotification } = useResponse();
  // One cell per selected grid column, each resolved the way the single-column path does.
  // Only a ctrl/cmd gesture can add or drop individual columns, so its committed snapshot rules;
  // a drag is exactly the native start..end span, which is current before the snapshot commits.
  const isToggleSelection = selectionSnapshot?.gesture === 'toggle';
  const selectedColumnCells = useMemo((): CellInfo[] => {
    if (isToggleSelection) {
      const firstCellByCol = new Map<number, CellInfo>();
      selectionSnapshot.cells.forEach(({ col, cell: gridCell }) => {
        if (!firstCellByCol.has(col)) {
          firstCellByCol.set(col, gridCell);
        }
      });
      return selectionSnapshot.cols
        .map(col => firstCellByCol.get(col))
        .filter((gridCell): gridCell is CellInfo => Boolean(gridCell));
    }
    if (!selectedCell) {
      return [];
    }
    const { start, end, cellInfo, endCellInfo } = selectedCell;
    const rowNumber = cellInfo.key?.replace(/\D/g, '') || String(start.i);
    return range(Math.min(start.j, end.j), Math.max(start.j, end.j) + 1).map(j => {
      if (j === start.j) {
        return cellInfo;
      }
      if (j === end.j && endCellInfo) {
        return endCellInfo;
      }
      const key = `${getColumnLetter(j)}${rowNumber}`;
      return cells?.[key] ?? { key, columnLegend: getColumnLetter(j) };
    });
  }, [isToggleSelection, selectedCell, selectionSnapshot, cells]);
  const isMultiColumnSelection = selectedColumnCells.length > 1;
  // The column the single-column actions target: whatever a ctrl/cmd toggle left selected, else the active cell.
  const anchorCell = isToggleSelection ? selectedColumnCells[0] : selectedCell?.cellInfo;
  const hasConfiguredColumnActions = Boolean(
    (allowAddMultipleColumns && addMultipleColumns) ||
      (allowCloneColumn && cloneColumn) ||
      allowCopyColumn ||
      allowDeleteColumn ||
      allowDeleteOnlySpecificColumn ||
      allowReorderColumns ||
      allowSortColumn ||
      onToggleFrozenPaneAtCell
  );

  useEffect(() => {
    if (!anchorCell || !displayColumnIndicator) {
      setIndicatorPosition(null);
      setIsIndicatorActionable(false);
      setShowIndicator(false);
      return;
    }

    setCell(anchorCell);
    if (!isMultiColumnSelection) {
      const start = selectedCell?.start;
      const cellKey = anchorCell.key ?? (start ? `${getColumnLetter(start.j)}${start.i}` : '');
      const cellRef = getCellRef(tableRef, cellKey);
      const hasColSpan = hasCellColSpan(cells, cellKey, cellRef);
      if (hasColSpan && (!allowMergedSelectionIndicatorSpan || hasConfiguredColumnActions)) {
        setIndicatorPosition(null);
        setIsIndicatorActionable(false);
        setShowIndicator(false);
        return;
      }
      if (cellRef) {
        // Check if this cell has the projection-to-ltm divider
        const hasProjectionToLtmDivider = cellRef.classList.contains('projection-to-ltm-divider');

        // Check if the previous cell has the projection-to-ltm divider
        const previousCell = cellRef.previousElementSibling as HTMLElement;
        const previousHasProjectionToLtmDivider = previousCell?.classList.contains('projection-to-ltm-divider');

        // Check if the next cell (LTM) has the projection-to-ltm divider
        const nextCell = cellRef.nextElementSibling as HTMLElement;
        const nextHasProjectionToLtmDivider = nextCell?.classList.contains('projection-to-ltm-divider');

        let adjustedWidth = cellRef.offsetWidth;
        let adjustedLeft = cellRef.offsetLeft;

        if (hasColSpan) {
          // Read-only merged cells can still show the visual range. Editable
          // sheets with column actions are hidden above to avoid ambiguous actions.
          adjustedWidth = cellRef.offsetWidth;
          adjustedLeft = cellRef.offsetLeft;
        } else if (hasProjectionToLtmDivider) {
          // This cell has the divider, shift its position right by 12.5px and reduce width by 12.5px on the left side
          adjustedLeft = cellRef.offsetLeft + 12.5;
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (previousHasProjectionToLtmDivider) {
          // The previous cell has the divider, reduce this cell's width by 12.5px on the left side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (nextHasProjectionToLtmDivider) {
          // The next cell (LTM) has the divider, reduce this cell's width by 12.5px on the right side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else {
          // For regular columns (including new projection columns), use the exact cell dimensions
          adjustedWidth = cellRef.offsetWidth;
          adjustedLeft = cellRef.offsetLeft;
        }

        setIndicatorPosition({
          left: adjustedLeft,
          width: adjustedWidth,
        });
        setIsIndicatorActionable(!hasColSpan);
        setShowIndicator(true);
      } else {
        setIndicatorPosition(null);
        setIsIndicatorActionable(false);
        setShowIndicator(false);
      }
    } else if (deleteMultipleColumnsFn) {
      // Multi-column selections only surface on sheets that opted into bulk column delete.
      const position = computeMultiColumnPosition(
        tableRef,
        selectedColumnCells.map(({ key }) => key ?? '').filter(Boolean)
      );
      setIndicatorPosition(position);
      setIsIndicatorActionable(false);
      setShowIndicator(Boolean(position));
    } else {
      setIndicatorPosition(null);
      setIsIndicatorActionable(false);
      setShowIndicator(false);
    }
  }, [
    anchorCell,
    selectedCell,
    displayColumnIndicator,
    cells,
    tableRef,
    hiddenColumns,
    allowMergedSelectionIndicatorSpan,
    hasConfiguredColumnActions,
    deleteMultipleColumnsFn,
    isMultiColumnSelection,
    selectedColumnCells,
  ]);

  const handleOnMoreOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const onCloseDropdown = (): void => setAnchorEl(null);

  const handleOnCopyClick = useCallback(() => {
    onCloseDropdown();
    if (!cell) {
      return;
    }
    const { columnLegend, key } = cell;
    const columnLetter = columnLegend ?? getLetterFromKey(key);
    if (columnLetter) {
      const valuesToCopy = getValuesToCopy(
        cells,
        columnLetter,
        true,
        format?.currency?.exchangeRate,
        format?.currency?.units
      );
      copyToClipboard(valuesToCopy, infoNotification, errorNotification);
    }
  }, [cell, cells, infoNotification, errorNotification, format]);

  const removeIndicator = useCallback(() => {
    setClearSelected(true);
    setSelectedCell(undefined);
    setIndicatorPosition(null);
    setIsIndicatorActionable(false);
    setShowIndicator(false);
  }, [setClearSelected, setSelectedCell]);

  const handleToggleFrozenPane = useCallback(() => {
    setAnchorEl(null);
    if (cell?.key && onToggleFrozenPaneAtCell) {
      const frozenPaneColumnLegend = frozenPaneCellKey?.replace(/\d+$/, '');
      const isCurrentBoundary = Boolean(cell.columnLegend && frozenPaneColumnLegend === cell.columnLegend);
      onToggleFrozenPaneAtCell(isCurrentBoundary && frozenPaneCellKey ? frozenPaneCellKey : String(cell.key));
      removeIndicator();
    }
  }, [cell?.columnLegend, cell?.key, frozenPaneCellKey, onToggleFrozenPaneAtCell, removeIndicator]);

  const columnIndex = useMemo(() => (cell ? resolveColumnIndex(cell) : null), [cell]);

  const hasAISuggestions = useMemo(() => {
    if (!cell || !cells) {
      return false;
    }
    const { columnLegend, key } = cell;
    const colLetter = columnLegend ?? getLetterFromKey(key);
    if (!colLetter) {
      return false;
    }
    return Object.entries(cells).some(([cellKey, c]) => {
      if (!cellKey.startsWith(colLetter)) {
        return false;
      }
      // PENDING findings only: a settled (accepted OR dismissed) column has
      // nothing left to bulk-accept/dismiss, so the AI menu entries hide.
      // is_accepted alone is wrong here - dismissed keeps is_accepted=false.
      return isPendingMeta((c as any).aiFieldMetadata);
    });
  }, [cell, cells]);

  const handleAcceptAllSuggestions = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (currentColumnId != null) {
        acceptAllFields(Number(currentColumnId));
      }
    }
  }, [cell, columnIndex, columns, acceptAllFields]);

  const handleDismissAllSuggestions = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (currentColumnId != null) {
        dismissAllFields(Number(currentColumnId));
      }
    }
  }, [cell, columnIndex, columns, dismissAllFields]);

  const sortedColumns = useMemo(() => (columns ? (sortBy(columns, ['order']) as Column[]) : []), [columns]);

  // One deletability rule for both the single DELETE entry and the bulk delete span,
  // so LTM/NTM, grouped, and protected columns are never deleted either way.
  const isColumnDeletable = useCallback(
    (index: number | null): boolean => {
      if (!(allowDeleteColumn || allowDeleteOnlySpecificColumn) || isNil(index) || index >= sortedColumns.length) {
        return false;
      }
      const column = sortedColumns[index];
      const isDeletable = Boolean(column && !column.isLTM && !column.isNTM && !('parentColumn' in column));
      const allowDelete = Boolean(allowDeleteColumn || allowConfirmAndDeleteColumn) && isDeletable;
      return allowDeleteOnlySpecificColumn ? Boolean(column.isDeleteableColumn) && allowDelete : allowDelete;
    },
    [allowConfirmAndDeleteColumn, allowDeleteOnlySpecificColumn, allowDeleteColumn, sortedColumns]
  );

  const showDeleteOption = useMemo(() => isColumnDeletable(columnIndex), [isColumnDeletable, columnIndex]);

  // Bulk-delete payload: every selected column resolved exactly like the single-column delete,
  // protected columns dropped, de-duplicated, ascending.
  const bulkDeleteIndexes = useMemo(() => {
    if (!deleteMultipleColumnsFn || !isMultiColumnSelection) {
      return [];
    }
    const indexes = selectedColumnCells
      .map(resolveColumnIndex)
      .filter((index): index is number => isColumnDeletable(index));
    return uniq(indexes).sort((a, b) => a - b);
  }, [deleteMultipleColumnsFn, isMultiColumnSelection, selectedColumnCells, isColumnDeletable]);

  const hideIndicator = useCallback(() => {
    setSelectedCell(null);
    setIndicatorPosition(null);
    setShowIndicator(false);
  }, [setSelectedCell]);

  const getColumnId = (
    cellColumnId: number | string | null | undefined,
    colIndex: number | null,
    cols: ReverseParsedFundOwnershipDetail[]
  ): number | string | null => {
    if (cellColumnId) {
      return cellColumnId;
    }
    if (cols && colIndex !== null) {
      return cols[colIndex]?.id ?? colIndex;
    }
    return colIndex;
  };

  // Accept / dismiss all wait while a review decision on this column is still saving.
  const columnReviewBusy = useSecurityReviewBusy(cell ? getColumnId(cell.columnId, columnIndex, columns) : null);

  const handleOnDeleteClick = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (handleDeleteColumn && showDeleteOption) {
        // If handleDeleteColumn expects 3 arguments based on its signature, it comes from ScalarSpreadsheet
        // If so, we will pass columnIndex, columnId, wb
        if (handleDeleteColumn.length === 4 && !isNil(currentColumnId) && workbook) {
          handleDeleteColumn(columnIndex, currentColumnId, workbook, hideIndicator);
        }
        // If it expects 1 argument, it comes from FeaturedSpreadsheet
        // If so, we will pass columnIndex
        if (handleDeleteColumn.length === 2 && columnIndex !== null) {
          handleDeleteColumn(columnIndex, hideIndicator);
        }
      }
    }
  }, [cell, handleDeleteColumn, workbook, columnIndex, showDeleteOption, columns, hideIndicator]);

  // The page owns confirmation, so the menu action hands over the payload immediately.
  const handleOnDeleteMultipleClick = useCallback(() => {
    onCloseDropdown();
    if (deleteMultipleColumnsFn && bulkDeleteIndexes.length > 0) {
      deleteMultipleColumnsFn(bulkDeleteIndexes);
      removeIndicator();
    }
  }, [deleteMultipleColumnsFn, bulkDeleteIndexes, removeIndicator]);

  const handleOnCloneClick = useCallback(() => {
    onCloseDropdown();
    if (cell && cloneColumn && columnIndex !== null) {
      if (!isNil(columnIndex)) {
        cloneColumn(columnIndex);
      }
    }
  }, [cell, cloneColumn, columnIndex]);

  const handleOnAddMultipleClick = (): void => {
    onCloseDropdown();
    if (addMultipleColumns) {
      addMultipleColumns(multipleValue);
    }
  };

  const currentColumn = columns && columnIndex !== null ? (columns[columnIndex] as Column) : null;
  const field = currentColumn?.fields?.[0];
  const columnKey = field ? Object.values(field)?.[0] : null;
  const fieldIsString = !!currentColumn?.isStringColumn;
  const fieldIsDate = !!currentColumn?.isDateColumn;

  const isSortableColumn = useMemo(
    () => !!allowSortColumn && !!currentColumn?.isSortableColumn,
    [allowSortColumn, currentColumn]
  );

  const SortDescription = useMemo(() => {
    if (isEmpty(sortedColumn)) {
      return SortDescIcon;
    }

    if (sortedColumn && columnKey && columnKey in sortedColumn) {
      return getStringValue(sortedColumn?.[columnKey]) === SORT_DESC ? SortAscIcon : SortDescIcon;
    }

    return SortDescIcon;
  }, [columnKey, sortedColumn]);

  const handleSortColumnClick = (): void => {
    onCloseDropdown();

    if (isSortableColumn && columnKey && sortColumnFn) {
      sortColumnFn(columnKey, fieldIsString, fieldIsDate);
    }
  };

  // With this function we will determine if do we have to show or not the dropdown menu on the column indicator
  const showMenuOptions = useMemo(
    () =>
      Boolean(onToggleFrozenPaneAtCell) ||
      allowCopyColumn ||
      allowReorderColumns ||
      showDeleteOption ||
      isSortableColumn ||
      hasAISuggestions,
    [
      allowCopyColumn,
      allowReorderColumns,
      isSortableColumn,
      showDeleteOption,
      hasAISuggestions,
      onToggleFrozenPaneAtCell,
    ]
  );
  const shouldShowColumnActions = showIndicator && isIndicatorActionable && !isMultiColumnSelection;
  const frozenPaneColumnLegend = frozenPaneCellKey?.replace(/\d+$/, '');
  const isCurrentFrozenPaneBoundary = Boolean(cell?.columnLegend && frozenPaneColumnLegend === cell.columnLegend);
  const freezeColumnLabel = isCurrentFrozenPaneBoundary ? 'Unfreeze this column' : 'Freeze this column';

  return (
    <StyledColumnIndicator
      id={`column-indicator-${(cell?.columnLegend ?? '') + 0}`}
      className={allowReorderColumns ? 'column-rearrange' : ''}
      allowReorderColumns={allowReorderColumns && isIndicatorActionable}
      style={{
        display: showIndicator && indicatorPosition ? undefined : 'none',
        left: indicatorPosition?.left,
        width: indicatorPosition?.width,
      }}>
      {showIndicator && isMultiColumnSelection && (
        <>
          <StyledMultiColumnLabel>{`${selectedColumnCells.length} columns`}</StyledMultiColumnLabel>
          {bulkDeleteIndexes.length > 0 && (
            <>
              <StyledMenuButton cursorType="pointer" aria-label="Column actions" onClick={handleOnMoreOptionsClick}>
                <StyledKeyboardArrowDownIcon fontSize="small" />
              </StyledMenuButton>
              <Popper open={open} anchorEl={anchorEl}>
                <StyledMenu
                  compactViewerOptions={false}
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  keepMounted
                  open={open}
                  onClose={onCloseDropdown}>
                  <StyledDeleteMenuItem onClick={handleOnDeleteMultipleClick}>
                    {`Delete ${bulkDeleteIndexes.length} columns`}
                  </StyledDeleteMenuItem>
                </StyledMenu>
              </Popper>
            </>
          )}
        </>
      )}
      {shouldShowColumnActions && allowReorderColumns && (
        <StyledMenuButton cursorType="move">
          <StyledMoreHorizIcon fontSize="small" />
        </StyledMenuButton>
      )}
      {shouldShowColumnActions && showMenuOptions && (
        <>
          <StyledMenuButton
            cursorType="pointer"
            emphasizeViewerFreezeAction={Boolean(onToggleFrozenPaneAtCell)}
            onClick={handleOnMoreOptionsClick}>
            <StyledKeyboardArrowDownIcon fontSize="small" />
          </StyledMenuButton>
          <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
            <StyledMenu
              compactViewerOptions={Boolean(onToggleFrozenPaneAtCell)}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              {...(!onToggleFrozenPaneAtCell
                ? {
                  PaperProps: {
                    style: {
                      minWidth: indicatorPosition?.width,
                    },
                  },
                }
                : {})}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={onCloseDropdown}>
              {onToggleFrozenPaneAtCell && (
                <MenuItem data-testid="toggle-frozen-columns" onClick={handleToggleFrozenPane} disableRipple>
                  <PushPinOutlined />
                  {freezeColumnLabel}
                  <Checkbox
                    aria-label={freezeColumnLabel}
                    checked={isCurrentFrozenPaneBoundary}
                    onChange={handleToggleFrozenPane}
                    onClick={event => event.stopPropagation()}
                    size="small"
                  />
                </MenuItem>
              )}
              {allowCopyColumn && (
                <MenuItem onClick={handleOnCopyClick} disableRipple>
                  COPY
                </MenuItem>
              )}
              {allowCloneColumn && (
                <MenuItem onClick={handleOnCloneClick} disableRipple>
                  CLONE
                </MenuItem>
              )}
              {allowAddMultipleColumns && addMultipleColumns && (
                <AddMultipleMenuItem
                  onAddMultiple={handleOnAddMultipleClick}
                  multipleValue={multipleValue}
                  setMultipleValue={setMultipleValue}
                />
              )}
              {hasAISuggestions && (
                <StyledAcceptAllMenuItem onClick={handleAcceptAllSuggestions} disabled={columnReviewBusy} disableRipple>
                  <Typography sx={{ ...(aiGradientTextSx as object), fontWeight: 600, fontSize: '0.875rem' }}>
                    ACCEPT ALL SUGGESTIONS
                  </Typography>
                </StyledAcceptAllMenuItem>
              )}
              {hasAISuggestions && (
                <StyledAcceptAllMenuItem
                  onClick={handleDismissAllSuggestions}
                  disabled={columnReviewBusy}
                  disableRipple>
                  <Typography sx={{ ...(aiGradientTextSx as object), fontWeight: 600, fontSize: '0.875rem' }}>
                    DISMISS ALL SUGGESTIONS
                  </Typography>
                </StyledAcceptAllMenuItem>
              )}
              {showDeleteOption && <StyledDeleteMenuItem onClick={handleOnDeleteClick}>DELETE</StyledDeleteMenuItem>}

              {isSortableColumn && (
                <MenuItem onClick={handleSortColumnClick}>
                  <SortDescription />
                </MenuItem>
              )}
            </StyledMenu>
          </Popper>
        </>
      )}
    </StyledColumnIndicator>
  );
};

export default ColumnIndicator;
